// Type definition for a single suggestion
/**
 * API - /chat/ask
 */

/**
 * API - /data/sample
 */

export interface Suggestion {
  text: string;
  id: string;
}

// Type definition for the API response
export interface GetIspmSuggestionsResponse {
  suggestions: Suggestion[];
}

// Type for a single row in the table where keys are column names and values can be table headers or data
export type DynamicRow = Record<string, string>;

const AskApiQueryConfidenceValues = ['high', 'low'] as const;

// Create the enum from the values
export const AskApiQueryConfidenceEnum = {
  HIGH: AskApiQueryConfidenceValues[0],
  LOW: AskApiQueryConfidenceValues[1],
} as const;

// Create the type from the array values
export type AskApiQueryConfidence =
  (typeof AskApiQueryConfidenceValues)[number];

const AskApiContentTypeValues = [
  'query',
  'sampleData',
  'text',
  'chart',
] as const;

// Create the enum from the values
export const AskApiContentTypeEnum = {
  QUERY: AskApiContentTypeValues[0],
  SAMPLE_DATA: AskApiContentTypeValues[1],
  TEXT: AskApiContentTypeValues[2],
  CHART: AskApiContentTypeValues[3],
} as const;

// Create the type from the array values
export type AskApiContentType = (typeof AskApiContentTypeValues)[number];

// Type for the detailed steps in the response
export type DetailedStep = string;

export enum ErrorTypeEnum {
  Copilot = 'Copilot',
  SQL = 'SQL',
  System = 'System',
}

export interface ErrorType {
  errorCode: string; // Error code if something went wrong
  errorMsg: string; // Error message to display
  type: ErrorTypeEnum;
}

export type ChartSpec = VegaliteChartSpec;

// Type for the links in the response
export interface Link {
  text: string;
  id: string;
}

// Type for the basic Ask API request (for general free-text queries)
export interface AskApiRequest {
  chatuuid: string | null;
  freeText: string;
  chartType?: 'vegalite';
}

// Define a new type that requires the freeText property
export interface RequestWithFreeText {
  freeText: string; // This ensures that freeText is required
}

interface BaseAskApi {
  chatuuid: string;
  text: string;
  // Below to track request for which this api response is.
  request?: (AskApiRequest | SampleDataApiRequest) & RequestWithFreeText;
}

export interface RandomAskApiResponse extends BaseAskApi {
  contentType: Extract<AskApiContentType, 'text'>;
}

interface BaseHighConfidenceAskApi extends BaseAskApi {
  confidence: Extract<AskApiQueryConfidence, 'high'>;
  query: string;
  links: Link[];
  detailedSteps: DetailedStep[];
}

export interface HighConfidenceTableAskApiResponse
  extends BaseHighConfidenceAskApi {
  contentType: Extract<AskApiContentType, 'sampleData'>;
  content: DynamicRow[];
}

export interface HighConfidenceChartAskApiResponse
  extends BaseHighConfidenceAskApi {
  contentType: Extract<AskApiContentType, 'chart'>;
  content: DynamicRow[];
  datasource_id?: number;
  chartSpec: ChartSpec;
}

interface BaseLowConfidenceAskApi extends BaseAskApi {
  confidence: Extract<AskApiQueryConfidence, 'low'>;
  contentType: Extract<AskApiContentType, 'query'>;
  links: Link[];
  detailedSteps: DetailedStep[];
  query: string;
}
export type LowConfidenceTabularAskApiResponse = BaseLowConfidenceAskApi;

export interface LowConfidenceChartAskApiResponse
  extends BaseLowConfidenceAskApi {
  datasource_id?: number;
  chartSpec: ChartSpec;
}

interface ErrorAskAPi {
  contentType: Extract<AskApiContentType, 'query'>;
  error: ErrorType;
}

/**
 * When error response will come -
 * contentType - will be query
 * links - will not be there
 * content - will not be there
 */
export type LowConfidenceChartAskApiErrorResponse = Omit<
  LowConfidenceChartAskApiResponse,
  'links' | 'contentType'
> &
  ErrorAskAPi;

export type LowConfidenceTabularAskApiErrorResponse = Omit<
  LowConfidenceChartAskApiResponse,
  'links' | 'contentType'
> &
  ErrorAskAPi;

export type HighConfidenceChartAskApiErrorResponse = Omit<
  HighConfidenceChartAskApiResponse,
  'content' | 'links' | 'contentType'
> &
  ErrorAskAPi;

export type HighConfidenceTableAskApiErrorResponse = Omit<
  HighConfidenceTableAskApiResponse,
  'content' | 'links' | 'contentType'
> &
  ErrorAskAPi;

export type RandomAskApiErrorResponse = Omit<
  HighConfidenceTableAskApiResponse,
  'content' | 'links' | 'contentType'
> &
  ErrorAskAPi;

export type GenericAskApiErrorResponse = Pick<BaseAskApi, 'chatuuid'> &
  Pick<ErrorAskAPi, 'error'>;

export type LowConfidenceAskApiErrorResponse =
  | LowConfidenceChartAskApiErrorResponse
  | LowConfidenceTabularAskApiErrorResponse;

export type HighConfidenceAskApiErrorResponse =
  | HighConfidenceChartAskApiErrorResponse
  | HighConfidenceTableAskApiErrorResponse;

export type AskApiErrorResponse =
  | LowConfidenceAskApiErrorResponse
  | HighConfidenceAskApiErrorResponse
  | RandomAskApiErrorResponse
  | GenericAskApiErrorResponse;

export type LowConfidenceAskApiResponse =
  | LowConfidenceChartAskApiResponse
  | LowConfidenceTabularAskApiResponse;

export type HighConfidenceAskApiResponse =
  | HighConfidenceChartAskApiResponse
  | HighConfidenceTableAskApiResponse;

export type AskApiResponse =
  | LowConfidenceAskApiResponse
  | HighConfidenceAskApiResponse
  | RandomAskApiResponse
  | AskApiErrorResponse;

export const AskApiResponseDefaultValues: AskApiResponse = {
  chatuuid: '',
  contentType: AskApiContentTypeEnum.TEXT,
  text: '',
};

// Type for the response when sample data is being generated

// Type for a request to generate sample data
export interface SampleDataApiRequest {
  chatuuid: string;
  query: string; // SQL query or similar to fetch sample data
  detailedSteps: DetailedStep[]; // Any specific steps to follow
  confidence: Exclude<AskApiQueryConfidence, 'high'>;
  chartSpec?: ChartSpec;
}

export type SampleDataTableResponse = Omit<
  HighConfidenceTableAskApiResponse,
  'confidence'
> & {
  confidence: Exclude<AskApiQueryConfidence, 'high'>;
};

export type SampleDataChartResponse = Omit<
  HighConfidenceChartAskApiResponse,
  'confidence'
> & {
  confidence: Exclude<AskApiQueryConfidence, 'high'>;
};

export type SampleDataErrorResponse =
  | (Omit<SampleDataTableResponse, 'links' | 'contentType'> & ErrorAskAPi)
  | (Omit<SampleDataTableResponse, 'links' | 'contentType'> & ErrorAskAPi)
  | (Pick<BaseAskApi, 'chatuuid'> & Pick<ErrorAskAPi, 'error'>);

export type SampleDataSuccessResponse =
  | SampleDataTableResponse
  | SampleDataChartResponse;

export type SampleDataAPIResponse =
  | SampleDataSuccessResponse
  | SampleDataErrorResponse;

/**
 * API: /data/download
 */

export interface DownloadDataRequest {
  query: string;
}

export interface DownloadDataSuccessResponse {
  status: string;
  message_code: string;
  message: string;
}

export interface DownloadDataErrorResponse {
  status: string;
  error: ErrorType;
}

/**
 * API: “/dashboard/list”
 */

export interface Dashboard {
  id: number;
  name: string;
}

export interface DashboardListSuccessResponse {
  dashboards: Dashboard[];
}

export interface DashboardListErrorResponse {
  status: string;
  error: ErrorType;
}
export type DashboardListApiResponse =
  | DashboardListSuccessResponse
  | DashboardListErrorResponse;

export interface SaveChartRequest {
  dashboard_id: number;
  datasource_id?: number;
  chart_name: string;
  chartSpec: ChartSpec;
}

interface SaveChartSuccessResponse {
  status: string;
  message_code: string;
  message: string;
}

interface SaveChartFailureResponse {
  status: string;
  error: ErrorType;
}

export type SaveChartResponse =
  | SaveChartSuccessResponse
  | SaveChartFailureResponse;

interface VegaliteDataSource {
  url: string;
}
interface VegaliteChartSpec {
  $schema: string;
  data: VegaliteDataSource;
  mark: string;
  encoding: VegaliteEncoding;
  title: string;
}

interface VegaliteEncoding {
  x: VegaliteAxisField;
  y: VegaliteAxisFieldWithTitle;
  tooltip: VegaliteTooltipField[];
}

interface VegaliteAxisField {
  field: string;
  type: string;
  sort?: string;
  axis?: VegaliteAxisOptions;
}

interface VegaliteAxisFieldWithTitle extends VegaliteAxisField {
  title?: string;
}

interface VegaliteAxisOptions {
  labelAngle?: number;
}

interface VegaliteTooltipField {
  field: string;
  type: string;
}
